import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuditNotificationProductLabel } from '../../utils/auditRuleCatalog';

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function AuditNotificationsButton({
  compact = false,
  embedded = false,
  unreadCount = 0,
  isRecipient = false,
  kardexAuditModuleEnabled = false,
  notifications = [],
  loadingNotifications = false,
  onOpen,
  onMarkRead,
  onMarkAllRead,
  onRefresh,
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      if (onOpen) await onOpen();
      if (onRefresh) await onRefresh();
    }
  };

  if (!kardexAuditModuleEnabled) return null;

  const unreadNotifications = notifications.filter((item) => !item.read_at);

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        aria-label="Alertas de auditoría"
        aria-expanded={open}
        onClick={handleToggle}
        className={`relative flex items-center justify-center rounded-full border shadow-sm transition-colors ${
          embedded
            ? 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
        } ${compact ? 'w-9 h-9' : 'w-11 h-11'}`}
      >
        <svg className={compact ? 'w-4 h-4' : 'w-5 h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden ${
            embedded ? 'w-[min(24rem,calc(100vw-2rem))]' : 'w-80'
          }`}
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-gray-900 text-sm">Alertas de auditoría</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {unreadCount > 0 ? `${unreadCount} sin leer` : 'Todas leídas'}
              </p>
            </div>
            {unreadNotifications.length > 0 && onMarkAllRead ? (
              <button
                type="button"
                onClick={onMarkAllRead}
                className="text-xs text-teal-700 hover:text-teal-900 font-medium whitespace-nowrap"
              >
                Marcar todas leídas
              </button>
            ) : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {!isRecipient ? (
              <div className="p-4 text-sm text-gray-600">
                No estás configurado como destinatario de alertas.
                <Link to="/audit/recipients" className="block mt-2 text-teal-700 hover:underline" onClick={() => setOpen(false)}>
                  Configurar destinatarios →
                </Link>
              </div>
            ) : loadingNotifications ? (
              <p className="p-4 text-sm text-gray-500">Cargando alertas…</p>
            ) : notifications.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No hay alertas recientes.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {notifications.map((item) => {
                  const isUnread = !item.read_at;
                  const productName = getAuditNotificationProductLabel(item);
                  const warehouseName = item.warehouse_name || item.data?.warehouse_name;
                  return (
                    <li
                      key={item.id}
                      className={`px-4 py-3 text-sm ${isUnread ? 'bg-amber-50/70' : 'bg-white'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {productName ? (
                            <p className={`text-teal-800 ${isUnread ? 'font-semibold' : 'font-medium'}`}>
                              {productName}
                            </p>
                          ) : null}
                          <p className={`text-gray-900 ${isUnread ? 'font-medium' : ''} ${productName ? 'mt-0.5' : ''}`}>
                            {item.message || item.data?.message || 'Alerta de auditoría'}
                          </p>
                          {warehouseName ? (
                            <p className="text-xs text-gray-500 mt-1">{warehouseName}</p>
                          ) : null}
                          <p className="text-xs text-gray-500 mt-1">{formatWhen(item.created_at)}</p>
                        </div>
                        {isUnread ? (
                          <button
                            type="button"
                            title="Marcar como leída"
                            onClick={() => onMarkRead?.(item.id)}
                            className="shrink-0 text-xs px-2 py-1 rounded border border-teal-200 text-teal-800 hover:bg-teal-50"
                          >
                            Leída
                          </button>
                        ) : (
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                            Leída
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-sm">
            <Link
              to="/audit/kardex"
              className="text-teal-700 hover:underline font-medium"
              onClick={() => setOpen(false)}
            >
              Ver todos los eventos →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
